/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Spinner,
  Center,
  Badge,
  Button,
} from "@chakra-ui/react";
import {
  FaStore,
  FaClipboardList,
  FaWallet,
  FaUserTie,
  FaArrowRight,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import { useSearch } from "../../context/SearchContext";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";

const typeIcons = {
  store: FaStore,
  plan: FaClipboardList,
  payment: FaWallet,
  employee: FaUserTie,
};

const typeLabels = {
  store: { ar: "متجر", en: "Store" },
  plan: { ar: "خطة", en: "Plan" },
  payment: { ar: "دفعة", en: "Payment" },
  employee: { ar: "موظف", en: "Employee" },
};

export default function SearchResults() {
  const { i18n } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();
  const { query, searchResults, isSearching } = useSearch();
  const navigate = useNavigate();

  const handleResultClick = (result) => {
    if (result.link) {
      // Navigate to the link with state containing the selected item data
      navigate(result.link, {
        state: {
          selectedItem: result.data,
          searchQuery: query,
          fromSearch: true
        }
      });
    }
  };

  return (
    <PageContainer title={isArabic ? "نتائج البحث" : "Search Results"}>
      {isSearching ? (
        <Center minH="60vh">
          <VStack spacing={4}>
            <Spinner color={colors.mainColor} size="xl" />
            <Text color={colors.subText}>
              {isArabic ? "جاري البحث..." : "Searching..."}
            </Text>
          </VStack>
        </Center>
      ) : !query.trim() ? (
        <Center minH="60vh">
          <Text color={colors.subText} fontSize="lg">
            {isArabic ? "أدخل كلمة البحث للبدء" : "Enter a search term to begin"}
          </Text>
        </Center>
      ) : searchResults.length === 0 ? (
        <Center minH="60vh">
          <VStack spacing={4}>
            <Text color={colors.subText} fontSize="lg">
              {isArabic ? "لم يتم العثور على نتائج" : "No results found"}
            </Text>
            <Text color={colors.subText} fontSize="sm">
              {isArabic 
                ? `لا توجد نتائج لـ "${query}"`
                : `No results for "${query}"`}
            </Text>
          </VStack>
        </Center>
      ) : (
        <VStack spacing={6} align="stretch">
          <Text color={colors.textColor} fontSize="lg">
            {isArabic 
              ? `تم العثور على ${searchResults.length} نتيجة لـ "${query}"`
              : `Found ${searchResults.length} results for "${query}"`}
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {searchResults.map((result, index) => {
              const IconComponent = typeIcons[result.type] || FaStore;
              const typeLabel = typeLabels[result.type] || { ar: "عنصر", en: "Item" };

              return (
                <UnifiedCard
                  key={index}
                  icon={IconComponent}
                  title={result.title}
                  badge={isArabic ? typeLabel.ar : typeLabel.en}
                  badgeColor={colors.mainColorLight}
                  onClick={() => handleResultClick(result)}
                >
                  {result.description && (
                    <Box mt={3}>
                      <Text color={colors.subText} fontSize="sm" mb={3} noOfLines={2}>
                        {result.description}
                      </Text>
                    </Box>
                  )}

                  <Button
                    size="sm"
                    bg={colors.buttonBg}
                    color={colors.buttonText}
                    _hover={{ bg: colors.buttonHover, transform: "translateY(-2px)", boxShadow: "md" }}
                    borderRadius="xl"
                    variant="solid"
                    fontWeight="semibold"
                    transition="all 0.3s"
                    width="100%"
                    rightIcon={<FaArrowRight />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResultClick(result);
                    }}
                  >
                    {isArabic ? "عرض التفاصيل" : "View Details"}
                  </Button>
                </UnifiedCard>
              );
            })}
          </SimpleGrid>
        </VStack>
      )}
    </PageContainer>
  );
}

